import { ReactNode, useEffect, useRef, useState } from 'react';

import { ResourceV2Modules } from 'dashboard/modules/Resource/model';
import ResourcePlaceholder from 'dashboard/modules/Resource/ResourcePlaceholder';
import ResourceInquiryV2, { ModuleResourceInquiryV2 } from 'dashboard/modules/ResourceInquiryV2';
import ResourceReportV2, { ModuleResourceReportV2 } from 'dashboard/modules/ResourceReportV2';
import ResourceVerificationV2, {
  ModuleResourceVerificationV2,
} from 'dashboard/modules/ResourceVerificationV2';
import { captureMessage } from 'lib/reporting';

export interface ResourceV2Props {
  module: ResourceV2Modules;
  resources: any;
  currentObjectToken: string;
}

const extractResourceId = (resource: any) => {
  if (typeof resource === 'object') {
    // TODO: check if resource is an instance of PlatformObject
    return resource.id;
  } else if (typeof resource === 'string') {
    return resource;
  }
};

const ResourceV2 = ({ module, resources, currentObjectToken }: ResourceV2Props) => {
  let resourceData;
  try {
    resourceData = module['variable-token']
      .split('.')
      .reduce((acc: any, key: string) => acc && acc[key], resources);
  } catch (error) {
    captureMessage('Failed to parse value.', 'warning', {
      error,
      component: 'ResourceV2',
    });
  }

  let resourceIds = [];
  if (resourceData) {
    if (resourceData instanceof Array) {
      resourceIds = resourceData.map((resource) => extractResourceId(resource));
    } else {
      resourceIds = [extractResourceId(resourceData)];
    }
  }

  const displayItems = resourceIds.filter(Boolean).map((resourceId) => {
    switch (module['field-key']) {
      case 'inquiries': {
        return (
          <ResourceInquiryV2
            module={module as ModuleResourceInquiryV2}
            currentObjectToken={currentObjectToken}
            inquiryToken={resourceId}
            featureFlags={{}}
          />
        );
      }
      case 'reports': {
        return (
          <ResourceReportV2
            module={module as ModuleResourceReportV2}
            currentObjectToken={currentObjectToken}
            reportToken={resourceId}
            featureFlags={{}}
          />
        );
      }
      case 'verifications': {
        return (
          <ResourceVerificationV2
            module={module as ModuleResourceVerificationV2}
            currentObjectToken={currentObjectToken}
            verificationToken={resourceId}
            featureFlags={{}}
          />
        );
      }
      default:
        return null;
    }
  });

  return (
    <ResourceV2ModuleContainer module={module} isLoading={!resources}>
      {displayItems}
    </ResourceV2ModuleContainer>
  );
};

function ResourceV2ModuleContainer({
  children,
  module,
  isLoading,
}: {
  children: ReactNode[];
  module: ResourceV2Modules;
  isLoading: boolean;
}) {
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const moduleChildren = Array.from(ref.current.children);

    const resizeObserver = new ResizeObserver(() => {
      if (moduleChildren.every((el) => el.clientHeight === 0 && el.clientWidth === 0)) {
        setShowPlaceholder(true);
      } else {
        setShowPlaceholder(false);
      }
    });

    moduleChildren.forEach((el) => resizeObserver.observe(el));

    return () => {
      moduleChildren.forEach((el) => resizeObserver.unobserve(el));
    };
  }, [children, module, isLoading, setShowPlaceholder]);

  return (
    <>
      {showPlaceholder || children.length === 0 ? (
        <ResourcePlaceholder module={module} isLoading={isLoading} />
      ) : null}
      <div ref={ref}>{children}</div>
    </>
  );
}

export default ResourceV2;
